/* eslint-disable @typescript-eslint/naming-convention */
import { IconStyle, Theme } from '@nextgen-web-framework/all';
import type { AlertMessageTheme, FAIconProps } from '@nextgen-web-framework/all';
import { SubscriptionResponseStatusEnum } from '@commerce-subscription-webcomponents/sdk';
import { ContainerStepProps } from '../subscription-plan/type/propTypes';

export const PaymentStatus = {
  INITIATED: 'INITIATED',
  VERIFIED: 'VERIFIED',
  FAILED: 'FAILED',
  INITIATED_FAILED: 'INITIATED_FAILED',
  CARD_EXPIRED: 'CARD_EXPIRED',
};

export const PaidStatus = [PaymentStatus.VERIFIED];
export const InprogressStatus = [PaymentStatus.INITIATED];
export const FailStatus = [PaymentStatus.INITIATED_FAILED, PaymentStatus.FAILED];
// TODO: After payment team is done with the changes then we need to add intiated in failStatus

export const PaymentStatusMapping = {
  [PaymentStatus.INITIATED]: 'Subscription.completeStatus.PENDING',
  [PaymentStatus.VERIFIED]: 'Subscription.completeStatus.VERIFIED',
  [PaymentStatus.FAILED]: 'Subscription.completeStatus.FAILED',
  [PaymentStatus.INITIATED_FAILED]: 'Subscription.completeStatus.INITIATED_FAILED',
};

export const LocalStorageKeys = {
  SUBSCRIPTION_ID: 'subscriptionId',
};

type StatusProps = {
  title: string;
  className: string;
  hideActionButtons: boolean;
  disabledState: boolean;
};
type StatusDataType = {
  [SubscriptionResponseStatusEnum: string]: StatusProps;
};

export const SubscriptionCardStatus: StatusDataType = {
  [SubscriptionResponseStatusEnum.Active]: {
    title: 'subscription.details.action.activeText',
    className: 'active',
    hideActionButtons: false,
    disabledState: false,
  },
  [SubscriptionResponseStatusEnum.Cancelled]: {
    title: 'subscription.subscriptionActiveCancelledTabs.cancelled.text',
    className: 'cancelled',
    hideActionButtons: true,
    disabledState: true,
  },
  [SubscriptionResponseStatusEnum.Paused]: {
    title: 'subscription.details.action.pausedText',
    className: 'paused',
    hideActionButtons: false,
    disabledState: true,
  },
  [SubscriptionResponseStatusEnum.Pending]: {
    title: 'subscription.details.action.pendingText',
    className: 'pending',
    hideActionButtons: true,
    disabledState: false,
  },
  [SubscriptionResponseStatusEnum.Completed]: {
    title: 'subscription.details.action.CompletedText',
    className: 'completed',
    hideActionButtons: true,
    disabledState: false,
  },
};

export enum statusActionFlow {
  PaymentDateModel = 'PaymentDateModel',
  ResumeSubscription = 'ResumeSubscription',
  RetryPayment = 'RetryPayment',
  RedirectToOrderDetails = 'RedirectToOrderDetails',
  ShowCardExpiryModal = 'ShowCardExpiryModal',
}

export type SubscriptionNotificatinProps = {
  title: string;
  description: string;
  theme: AlertMessageTheme;
  icon: FAIconProps;
  chevronClickType?: statusActionFlow;
};

export enum AlertTypeEnum {
  InitiatedPayment = 'InitiatedPayment',
  VerifiedPayment = 'VerifiedPayment',
  FailedPayment = 'FailedPayment',
  InitiatedFailedPayment = 'InitiatedFailedPayment',
  CardExpired = 'CartExpired',
  PausedSubscription = 'PausedSubscription',
  CancelledSubscription = 'CancelledSubscription',
  CompletedSubscription = 'CompletedSubscription',
  OrderInCart = 'OrderInCart',
  OrderPurchased = 'OrderPurchased',
  OrderNotPurchased = 'OrderNotPurchased',
  LastOrderFailed = 'LastOrderFailed',
  PaymentPending = 'PaymentPending',
  PaymentAwaiting = 'PaymentAwaiting',
  PaymentFailed = 'PaymentFailed',
}

type SubscriptionAlertDataType = {
  [AlertTypeEnum: string]: SubscriptionNotificatinProps;
};
export const subscriptionAlertData: SubscriptionAlertDataType = {
  [AlertTypeEnum.VerifiedPayment]: {
    title: 'subscription.ManageSubscription.SubscriptionCardNextPaymentDateText',
    description: 'subscription.ManageSubscription.alertMessage.NextPaymentDateText',
    theme: Theme.ACTION,
    icon: {
      iconName: 'truck',
      theme: Theme.PURPLE,
      iconStyle: IconStyle.SOLID,
    },
    chevronClickType: statusActionFlow.PaymentDateModel,
  },
  [AlertTypeEnum.InitiatedPayment]: {
    title: '',
    description: 'subscription.ManageSubscription.transactionValidationInProgress.Text',
    theme: Theme.WARNING,
    icon: { iconName: 'triangle-exclamation', theme: Theme.WARNING },
  },
  [AlertTypeEnum.InitiatedFailedPayment]: {
    title: 'subscription.ManageSubscription.Retry.Text',
    description: 'subscription.ManageSubscription.errorOccurredWithPaymentUsed.Text',
    theme: Theme.ERROR,
    icon: {
      iconName: 'square-exclamation',
      iconStyle: IconStyle.REGULAR,
      theme: Theme.ERROR,
    },
    chevronClickType: statusActionFlow.RetryPayment,
  },
  [AlertTypeEnum.FailedPayment]: {
    title: 'subscription.ManageSubscription.Retry.Text',
    description: 'subscription.ManageSubscription.errorOccurredWithPaymentUsed.Text',
    theme: Theme.ERROR,
    icon: {
      iconName: 'square-exclamation',
      iconStyle: IconStyle.SOLID,
      theme: Theme.ERROR,
    },
    chevronClickType: statusActionFlow.RetryPayment,
  },
  [AlertTypeEnum.CancelledSubscription]: {
    title: 'subscription.ManageSubscription.subscriptionCancelled.Text',
    description: 'subscription.ManageSubscription.subscriptionCancelledDate.Text',
    theme: Theme.GRAY,
    icon: { iconName: 'file-excel', theme: Theme.BLACK },
  },
  [AlertTypeEnum.PausedSubscription]: {
    title: 'subscription.ManageSubscription.resumeSubscription.Text',
    description: 'subscription.ManageSubscription.yourSubscriptionIsCurrentlyPaused.Text',
    theme: Theme.WARNING,
    icon: { iconName: 'triangle-exclamation', theme: Theme.WARNING },
    chevronClickType: statusActionFlow.ResumeSubscription,
  },
  [AlertTypeEnum.CompletedSubscription]: {
    title: 'subscription.ManageSubscription.completedSubscription.Text',
    description: '',
    theme: Theme.SUCCESS,
    icon: { iconName: 'check-circle', theme: Theme.SUCCESS },
  },
  [AlertTypeEnum.OrderInCart]: {
    title: 'subscription.ManageSubscription.OrderInCartText.Text',
    description: 'subscription.ManageSubscription.PurchaseBy',
    // @TODO: Change theme to Blue once framework team added support in AlertMessage component
    theme: Theme.ACTION,
    icon: { iconName: 'hourglass-half', theme: Theme.PURPLE, iconStyle: IconStyle.SOLID },
  },
  [AlertTypeEnum.OrderPurchased]: {
    title: 'subscription.ManageSubscription.OrderPurchased.Text',
    description: 'subscription.ManageSubscription.NextPurchaseBy',
    theme: Theme.SUCCESS,
    icon: { iconName: 'check', theme: Theme.SUCCESS },
  },
  [AlertTypeEnum.OrderNotPurchased]: {
    title: 'subscription.ManageSubscription.OrderNotPurchased.Text',
    description: 'subscription.ManageSubscription.PurchaseBy',
    theme: Theme.WARNING,
    icon: { iconName: 'money-check-dollar-pen', theme: Theme.WARNING },
  },
  [AlertTypeEnum.LastOrderFailed]: {
    title: 'subscription.ManageSubscription.LastOrderFailed.Text',
    description: 'subscription.ManageSubscription.RetryPayment.Text',
    theme: Theme.ERROR,
    icon: { iconName: 'square-exclamation', theme: Theme.ERROR },
    chevronClickType: statusActionFlow.RedirectToOrderDetails,
  },
  [AlertTypeEnum.PaymentAwaiting]: {
    title: 'subscription.ManageSubscription.ManualSOP.PaymentAwaited.Text',
    description: 'subscription.ManageSubscription.ManualSOP.LastOrderUnderReview.Text',
    theme: Theme.WARNING,
    icon: { iconName: 'triangle-exclamation', theme: Theme.WARNING },
    chevronClickType: statusActionFlow.RedirectToOrderDetails,
  },
  [AlertTypeEnum.PaymentPending]: {
    title: '',
    description: 'subscription.ManageSubscription.transactionValidationInProgress.Text',
    theme: Theme.WARNING,
    icon: { iconName: 'triangle-exclamation', theme: Theme.WARNING },
  },
  [AlertTypeEnum.PaymentFailed]: {
    title: 'subscription.ManageSubscription.ManualSOP.PaymentFailed.Text',
    description: '',
    theme: Theme.ERROR,
    icon: { iconName: 'square-exclamation', theme: Theme.ERROR },
    chevronClickType: statusActionFlow.RedirectToOrderDetails,
  },
  [AlertTypeEnum.CardExpired]: {
    title: 'subscription.ManageSubscription.CardExpired.updatePaymentMethod.Text',
    description: 'subscription.ManageSubscription.CardExpired.description.Text',
    theme: Theme.WARNING,
    icon: { iconName: 'square-exclamation', theme: Theme.WARNING },
    chevronClickType: statusActionFlow.ShowCardExpiryModal,
  },
};

export const subscriptionStepsList: ContainerStepProps[] = [
  {
    id: 0,
    isAccordianOpened: true,
    isVisited: true,
    isCompleted: false,
  },
  {
    id: 1,
    isAccordianOpened: false,
    isVisited: false,
    isCompleted: false,
  },
  {
    id: 2,
    isAccordianOpened: false,
    isVisited: false,
    isCompleted: false,
  },
];

export enum SopConstantsEnum {
  sopEditProfilePageBackRedirectUrl = 'sopEditProfilePageBackRedirectUrl',
}

export enum SopLocalStorageKeys {
  SopBackUrl = 'sop-back-navigation-url',
}
